// The one date formatter for display. Every human-readable date in the UI comes
// through here so there is a single place the timezone rule lives.
//
// The rule: a date-only value (`2018-08-14`) is a CALENDAR date, not an instant. It has
// no timezone, and the family reading it means the 14th wherever they are sitting. But
// `new Date('2018-08-14')` parses it as UTC midnight while `toLocaleDateString` renders
// in LOCAL time, so anywhere west of UTC it prints the day before: US Mountain time sees
// 6pm on the 13th and the UI says "Aug 13, 2018". That is every date-only value, for
// every user at a negative UTC offset, all year round (it is not a DST edge case).
//
// So date-only input is pinned to UTC on both sides: parsed as UTC midnight, rendered
// with `timeZone: 'UTC'`. A full timestamp is a real instant and is rendered in local
// time, which is what a timestamp means. Unparseable input passes through untouched
// rather than becoming "Invalid Date" on the page.
//
// The locale is pinned to en-US rather than left to the viewer so the rendering is
// deterministic in tests and matches the rest of the app's copy.

/** True for a bare `YYYY-MM-DD` — a calendar date carrying no time and no zone. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * True only for a `YYYY-MM-DD` that names a day that actually exists.
 *
 * The shape test alone is not enough, because `Date` SILENTLY ROLLS OVER an
 * out-of-range day: `2018-02-31` parses, and comes back as March 3rd. Left unchecked
 * that turns a typo into a confident wrong answer on screen, which is worse than
 * showing the family the odd string they have stored. So the parse is round-tripped
 * back to `YYYY-MM-DD` and only an exact match counts.
 *
 * Exported because two places have to agree on the same question: this module decides
 * whether a value can be DISPLAYED as a date, and the asset form decides whether it can
 * be EDITED in a date picker (a picker renders blank for anything it cannot parse, and
 * would then save that blank over the family's data). One predicate, one answer.
 */
export function isCalendarDate(value: string): boolean {
  if (!DATE_ONLY.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === value;
}

const DISPLAY: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };

/** Renders an ISO date as e.g. "Aug 14, 2018"; passes unparseable input through. */
export function fmtDate(iso: string): string {
  if (!iso) return iso;
  if (DATE_ONLY.test(iso)) {
    if (!isCalendarDate(iso)) return iso;
    const d = new Date(`${iso}T00:00:00Z`);
    return d.toLocaleDateString('en-US', { ...DISPLAY, timeZone: 'UTC' });
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', DISPLAY);
}
